import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../../lib/utils.js';
import { Button } from '../../../components/ui/Button.js';

/**
 * An icon button for the editor toolbar. mousedown is cancelled so clicking it never moves focus
 * out of a text element being edited — the browser keeps the text selection the button acts on.
 */
export function ToolButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'h-10 w-10 md:h-8 md:w-8',
        active && 'bg-accent-soft text-accent-soft-fg hover:bg-accent-soft',
      )}
    >
      {children}
    </Button>
  );
}

/**
 * A swatch that opens the native color picker. Only the picker's final `change` is reported —
 * not every `input` while the user drags around in it — so one color pick is one edit (and one
 * undo step) rather than dozens.
 */
export function ColorInput({
  label,
  value,
  onChange,
  icon,
}: {
  label: string;
  value: string | null;
  onChange: (color: string) => void;
  icon?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const handleInput = () => setPreview(input.value);
    const handleChange = () => {
      setPreview(null);
      onChangeRef.current(input.value);
    };
    input.addEventListener('input', handleInput);
    input.addEventListener('change', handleChange);
    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('change', handleChange);
    };
  }, []);

  // Uncontrolled (so the native picker isn't fought mid-drag); follow outside changes here.
  useEffect(() => {
    if (inputRef.current && value) inputRef.current.value = value;
  }, [value]);

  const shown = preview ?? value;
  return (
    <label
      title={label}
      className="relative inline-flex h-10 cursor-pointer items-center gap-1 rounded-lg px-1.5 text-text hover:bg-surface-2 md:h-8"
    >
      {icon}
      <span
        className="h-4 w-4 rounded-sm border border-border"
        style={{
          background:
            shown ?? 'repeating-linear-gradient(45deg, transparent 0 3px, var(--border) 3px 4px)',
        }}
      />
      <input
        ref={inputRef}
        type="color"
        aria-label={label}
        defaultValue={value ?? '#000000'}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </label>
  );
}

export function ToolbarDivider() {
  return <span aria-hidden className="mx-1 h-6 w-px shrink-0 bg-border" />;
}

/**
 * The selected element's rotation in degrees. Typed values apply as they become valid numbers
 * (the parent coalesces them into one undo step); anything is wrapped into 0-359.9 on blur.
 */
export function RotationInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (degrees: number) => void;
}) {
  const [text, setText] = useState(String(value));
  const focused = useRef(false);

  // Follow the element (a drag on the canvas handle, undo) unless the user is mid-typing.
  useEffect(() => {
    if (!focused.current) setText(String(value));
  }, [value]);

  return (
    <label className="flex h-10 items-center gap-1 rounded-lg border border-border px-2 text-xs text-muted md:h-8">
      <span aria-hidden>↻</span>
      <input
        type="number"
        step={1}
        value={text}
        aria-label="Rotation in degrees"
        title="Rotation (degrees)"
        className="w-12 bg-transparent text-right text-sm text-text outline-none"
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={() => {
          focused.current = false;
          setText(String(value));
        }}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value.trim() !== '' && Number.isFinite(n)) onChange(n);
        }}
      />
      <span>°</span>
    </label>
  );
}
